import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { FaPaperPlane, FaTimes } from 'react-icons/fa';
import { playerAPI } from '../../services/api';
import PlayerInviteSearch from './PlayerInviteSearch';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem 1.35rem 1.35rem;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  color: #0f172a;
`;

const CloseBtn = styled.button`
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  color: #475569;
`;

const Hint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.45;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  background: #fff;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  resize: vertical;
  margin-bottom: 0.75rem;
  box-sizing: border-box;
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 10px;
  background: #0f766e;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TargetPlayer = styled.div`
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 0.75rem;
  font-weight: 700;
  color: #0f172a;
`;

/**
 * @param {object} props
 * @param {string} [props.teamId] — fixed team (My teams flow)
 * @param {string} [props.teamName]
 * @param {Array<{ teamId: string, teamName?: string }>} [props.invitableTeams] — picker when inviting from directory
 * @param {string} [props.invitedPlayerId] — preset player from directory (skip search)
 * @param {string} [props.invitedPlayerName]
 * @param {() => void} props.onClose
 * @param {() => void} [props.onSuccess]
 */
export default function InvitePlayerModal({
  teamId: fixedTeamId,
  teamName: fixedTeamName,
  invitableTeams = [],
  invitedPlayerId: presetPlayerId,
  invitedPlayerName,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(
    presetPlayerId ? { id: presetPlayerId, fullName: invitedPlayerName } : null
  );
  const [message, setMessage] = useState('');
  const [pickedTeamId, setPickedTeamId] = useState(fixedTeamId || '');

  const teamOptions = useMemo(() => {
    if (fixedTeamId) {
      return [{ teamId: fixedTeamId, teamName: fixedTeamName }];
    }
    return invitableTeams.filter((row) => row.teamId);
  }, [fixedTeamId, fixedTeamName, invitableTeams]);

  useEffect(() => {
    if (!pickedTeamId && teamOptions.length === 1) {
      setPickedTeamId(teamOptions[0].teamId);
    }
  }, [teamOptions, pickedTeamId]);

  const activeTeamId = fixedTeamId || pickedTeamId;
  const activeTeamName =
    fixedTeamName ||
    teamOptions.find((row) => String(row.teamId) === String(activeTeamId))?.teamName;

  const inviteMutation = useMutation(
    (payload) => {
      const body = {
        message: payload.body?.message || undefined,
        invitedPlayerId: payload.body?.invitedPlayerId || undefined,
        email: payload.body?.email || undefined,
        seasonId: payload.seasonId || undefined,
      };
      return playerAPI.createRegistrationInvitation(payload.teamId, body);
    },
    {
      onSuccess: (res) => {
        if (res?.success === false) {
          toast.error(res?.message || t('playerTeamsPage.toastInviteFailed'));
          return;
        }
        toast.success(
          t(
            'playerTeamsPage.toastRegistrationInviteSent',
            'Invitation sent. They must pay their half to join the roster.'
          )
        );
        onSuccess?.();
        onClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || t('playerTeamsPage.toastInviteFailed'));
      },
    }
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSend = selected?.id && activeTeamId;

  const sendInvite = (body) => {
    if (!activeTeamId) return;
    inviteMutation.mutate({
      teamId: activeTeamId,
      body: {
        ...body,
        message: message.trim() || undefined,
      },
    });
  };

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Modal role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('playerTeamsPage.invitePlayerTitle')}</Title>
          <CloseBtn type="button" onClick={onClose} aria-label={t('playerTeamsPage.close')}>
            <FaTimes aria-hidden />
          </CloseBtn>
        </Header>

        {teamOptions.length > 1 ? (
          <>
            <Hint>{t('playerTeamsPage.invitePickTeamHint')}</Hint>
            <Select
              value={pickedTeamId}
              onChange={(e) => {
                setPickedTeamId(e.target.value);
                if (!presetPlayerId) setSelected(null);
              }}
            >
              <option value="">{t('playerTeamsPage.invitePickTeamPlaceholder')}</option>
              {teamOptions.map((row) => (
                <option key={row.teamId} value={row.teamId}>
                  {row.teamName || row.teamId}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <Hint>
            {activeTeamName
              ? t('playerTeamsPage.invitePlayerHintNamed', { team: activeTeamName })
              : t('playerTeamsPage.invitePlayerHintSearch')}
          </Hint>
        )}

        {presetPlayerId ? (
          <TargetPlayer>
            {invitedPlayerName || t('playerTeamsPage.playerFallback')}
          </TargetPlayer>
        ) : (
          <PlayerInviteSearch
            mode="teams"
            teamId={activeTeamId}
            disabled={!activeTeamId || inviteMutation.isLoading}
            selectedRowId={selected?.id}
            onSelectRow={(row) =>
              setSelected({
                id: row.id,
                fullName: row.fullName || [row.firstName, row.lastName].filter(Boolean).join(' '),
              })
            }
            onInviteEmail={(email) =>
              sendInvite({ email })
            }
          />
        )}

        {canSend ? (
          <>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('playerTeamsPage.inviteMessagePlaceholder')}
              maxLength={1000}
            />
            <SendBtn
              type="button"
              disabled={inviteMutation.isLoading}
              onClick={() =>
                sendInvite({
                  invitedPlayerId: selected.id,
                })
              }
            >
              <FaPaperPlane aria-hidden />
              {inviteMutation.isLoading
                ? t('playerTeamsPage.sending')
                : t('playerTeamsPage.sendInvitation')}
            </SendBtn>
          </>
        ) : null}
      </Modal>
    </Overlay>
  );
}
